import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { supabase } from '../lib/supabase';
import { BrandMark } from '../components/BrandMark';
import { LanguageSwitcher } from '../components/LanguageSwitcher';

interface Status { status: string; ahead?: number; estimate_min?: number }

export function QueuePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const storeKey = `lpt_queue_${slug}`;

  const [entryId, setEntryId] = useState<string | null>(() => localStorage.getItem(storeKey));
  const [name, setName] = useState('');
  const [party, setParty] = useState('2');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<Status | null>(null);

  const refresh = useCallback(async (id: string) => {
    const { data } = await supabase.rpc('waitlist_status', { p_entry_id: id });
    if (data) setStatus(data as Status);
  }, []);

  useEffect(() => {
    if (!entryId) return;
    void refresh(entryId);
    const iv = window.setInterval(() => void refresh(entryId), 15000);
    return () => window.clearInterval(iv);
  }, [entryId, refresh]);

  async function leave() {
    if (!entryId) return;
    await supabase.rpc('cancel_waitlist', { p_entry_id: entryId });
    localStorage.removeItem(storeKey);
    void refresh(entryId);
  }

  async function join(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!slug) return;
    setBusy(true);
    const { data, error } = await supabase.rpc('join_waitlist', {
      p_slug: slug,
      p_name: name,
      p_party: Number(party) || 1,
      p_phone: phone
    });
    setBusy(false);
    if (!error && data) {
      const id = (data as { entry_id: string }).entry_id;
      localStorage.setItem(storeKey, id);
      setEntryId(id);
    }
  }

  return (
    <div className="diner">
      <header className="diner__header">
        <span className="brand">
          <BrandMark className="brand__mark" />
          <span className="brand__name">{t('queue.title')}</span>
        </span>
        <LanguageSwitcher />
      </header>

      <main className="diner__main">
        {!entryId ? (
          <form onSubmit={join} className="form">
            <label className="field">
              <span>{t('queue.name')}</span>
              <input required value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <div className="form-row">
              <label className="field">
                <span>{t('queue.party')}</span>
                <input type="number" min={1} value={party} onChange={(e) => setParty(e.target.value)} />
              </label>
              <label className="field">
                <span>{t('queue.phone')}</span>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" />
              </label>
            </div>
            <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
              {busy ? t('queue.joining') : t('queue.join')}
            </button>
          </form>
        ) : (
          <div className="queue-status">
            {status?.status === 'seated' ? (
              <h2 className="hero__title" style={{ fontSize: 26 }}>{t('queue.seated')}</h2>
            ) : status?.status === 'cancelled' ? (
              <p>{t('queue.cancelled')}</p>
            ) : status?.status === 'not_found' ? (
              <p>{t('queue.not_found')}</p>
            ) : (
              <>
                <p className="muted-note">{t('queue.waiting')}</p>
                <div className="stat" style={{ marginBottom: 12 }}>
                  <span className="stat__value">
                    {status?.ahead === 0 ? t('queue.ahead_zero') : `${status?.ahead ?? '—'} ${t('queue.ahead')}`}
                  </span>
                </div>
                {status?.estimate_min != null && status.estimate_min > 0 && (
                  <p>{t('queue.estimate')}: ~{status.estimate_min} {t('queue.min')}</p>
                )}
                <button type="button" className="btn btn--ghost btn--sm" style={{ marginTop: 16 }} onClick={() => void leave()}>
                  {t('queue.leave')}
                </button>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
